import { AppRouterInstance } from "next/dist/shared/lib/app-router-context.shared-runtime";
import { useRefStore } from "../store/ref-store";
import { gsap, ScrollTrigger } from "@/utils/gsap";
import { getScroller } from "@/components/common/ScrollManager/useScrollStore";

import TransitionStyles from "./components/Transition/Transition.module.scss";
import BurgerStyles from "@/components/layout/Header/Burger/Burger.module.scss";

const wrapperSelector = `.${TransitionStyles.wrapper}`;
const logoSelector = `.${TransitionStyles.logo}`;

let activeToken = 0;
let activeTimeline: gsap.core.Timeline | null = null;

if (typeof window !== "undefined") {
  window.addEventListener("popstate", () => {
    try {
      ScrollTrigger.getAll().forEach((st) => st.kill());
    } catch {}
  });
}

const invalidatePreviousTransition = () => {
  activeToken += 1;
  if (activeTimeline) {
    try {
      activeTimeline.kill();
    } catch {}
    activeTimeline = null;
  }
  return activeToken;
};

export const popStateIn = () => {
  useRefStore.setState({ fromPopState: false });
  invalidatePreviousTransition();

  const container: HTMLElement | null =
    document.querySelector(".generalPagesWrap");
  const mainWebWrapper: HTMLElement | null =
    document.querySelector(".mainWebWrapper");

  if (
    mainWebWrapper &&
    mainWebWrapper.style.backgroundColor !== "var(--background)"
  ) {
    mainWebWrapper.style.backgroundColor = "var(--background)";
  }

  const burgerElement: HTMLElement | null = document.querySelector(
    `.${BurgerStyles.burger}`
  );

  if (burgerElement) {
    const burgerElementColor = getComputedStyle(
      burgerElement
    ).getPropertyValue("--BurguerBackground");

    if (burgerElementColor !== "#4c2882") {
      gsap.set(`.${BurgerStyles.burger}`, {
        "--BurguerBackground": "var(--Primary-300, #4c2882)",
      });
    }
  }

  const wrapper: HTMLElement | null = document.querySelector(
    `.${TransitionStyles.wrapper}`
  );

  window.scrollTo({ top: 0, behavior: "instant" });

  if (wrapper) {
    wrapper.style.opacity = "0";
  }

  if (container) {
    container.style.overflowY = "";
    container.style.maxHeight = "";
    container.style.maxWidth = "";
  }
  document.body.style.overflowY = "";
  document.body.style.maxHeight = "";
  document.body.style.maxWidth = "";
};

export const animatePageIn = () => {
  const token = invalidatePreviousTransition();

  const container: HTMLElement | null =
    document.querySelector(".generalPagesWrap");
  const mainWebWrapper: HTMLElement | null =
    document.querySelector(".mainWebWrapper");

  if (
    mainWebWrapper &&
    mainWebWrapper.style.backgroundColor !== "var(--background)"
  ) {
    mainWebWrapper.style.backgroundColor = "var(--background)";
  }

  const burgerElement: HTMLElement | null = document.querySelector(
    `.${BurgerStyles.burger}`
  );

  if (burgerElement) {
    const burgerElementColor = getComputedStyle(
      burgerElement
    ).getPropertyValue("--BurguerBackground");

    if (burgerElementColor !== "#4c2882") {
      gsap.set(`.${BurgerStyles.burger}`, {
        "--BurguerBackground": "var(--Primary-300, #4c2882)",
      });
    }
  }

  const scroller = getScroller();
  scroller?.stop();

  const startAnimation = () => {
    if (token !== activeToken) return;

    const tl = gsap.timeline();
    activeTimeline = tl;

    tl.fromTo(
      logoSelector,
      {
        scale: 1,
        xPercent: -50,
        yPercent: -50,
      },
      {
        scale: 200,
        xPercent: 2600,
        yPercent: 3400,
        duration: 0.6,
        ease: "circ.in",
      }
    )
      .fromTo(
        container,
        {
          opacity: 0,
        },
        {
          opacity: 1,
          duration: 0.4,
          ease: "power2.in",
        },
        "<"
      )
      .fromTo(
        wrapperSelector,
        {
          opacity: 1,
        },
        {
          opacity: 0,
          duration: 0.4,
          ease: "power2.in",
          onComplete: () => {
            if (token !== activeToken) return;

            if (container) {
              container.style.overflowY = "";
              container.style.maxHeight = "";
              container.style.maxWidth = "";
            }
            document.body.style.overflowY = "";
            document.body.style.maxHeight = "";
            document.body.style.maxWidth = "";

            requestAnimationFrame(() => {
              if (token !== activeToken) return;
              scroller?.start();
              ScrollTrigger.refresh();
            });
          },
        },
        "<+=75%"
      );
  };

  requestAnimationFrame(() => {
    if (token !== activeToken) return;
    scroller?.reset();
    requestAnimationFrame(() => {
      if (token !== activeToken) return;
      ScrollTrigger.refresh();
      startAnimation();
    });
  });
};

export const animatePageOut = (
  href: string,
  router: AppRouterInstance,
  fromNav: boolean,
  onClick?: () => void
) => {
  const token = invalidatePreviousTransition();

  try {
    ScrollTrigger.getAll().forEach((st) => st.kill());
  } catch {}

  const container: HTMLElement | null =
    document.querySelector(".generalPagesWrap");

  router.prefetch(href);

  if (fromNav) {
    fromNavOut(router, href, container, token, onClick);
    return;
  }

  defaultOut(router, href, container, token);
};

const defaultOut = (
  router: AppRouterInstance,
  href: string,
  container: HTMLElement | null,
  token: number
) => {
  const tl = gsap.timeline();
  activeTimeline = tl;

  tl.fromTo(
    wrapperSelector,
    {
      opacity: 0,
    },
    {
      opacity: 1,
      duration: 0.4,
      ease: "power2.out",
    }
  ).fromTo(
    logoSelector,
    {
      scale: 200,
      xPercent: 2600,
      yPercent: 3400,
    },
    {
      scale: 1,
      xPercent: -50,
      yPercent: -50,
      duration: 0.6,
      ease: "circ.out",
      onComplete: () => {
        if (token !== activeToken) return;

        if (container) {
          container.style.overflowY = "hidden";
          container.style.maxHeight = "100vh";
          container.style.maxWidth = "100vw";
        }
        document.body.style.overflowY = "hidden";
        document.body.style.maxHeight = "100vh";
        document.body.style.maxWidth = "100vw";

        requestAnimationFrame(() => {
          if (token !== activeToken) return;
          requestAnimationFrame(() => {
            if (token !== activeToken) return;
            router.push(href);
          });
        });
      },
    },
    "<+=25%"
  );
};

const fromNavOut = (
  router: AppRouterInstance,
  href: string,
  container: HTMLElement | null,
  token: number,
  onClick?: () => void
) => {
  const navItemsSelectors = [
    ".container-header > a",
    ".container-header > div > div",
    ".container-header > div > button",
    ".container-header > nav > div",
  ];

  const tl = gsap.timeline();
  activeTimeline = tl;

  tl.to([...navItemsSelectors], {
    opacity: 0,
    duration: 0.25,
    ease: "power2.out",
  })
    .fromTo(
      wrapperSelector,
      {
        opacity: 0,
      },
      {
        opacity: 1,
        duration: 0.4,
        delay: 0.125,
        ease: "power2.out",
        onComplete: () => {
          if (token !== activeToken) return;
          onClick?.();
        },
      }
    )
    .fromTo(
      logoSelector,
      {
        scale: 200,
        xPercent: 2600,
        yPercent: 3400,
      },
      {
        scale: 1,
        xPercent: -50,
        yPercent: -50,
        duration: 0.6,
        ease: "circ.out",
      },
      "<+=25%"
    )
    .fromTo(
      ".generalPagesWrap",
      {
        opacity: 0,
      },
      {
        opacity: 1,
        duration: 0.6,
        ease: "power2.out",
        onComplete: () => {
          if (token !== activeToken) return;

          gsap.set([...navItemsSelectors], { opacity: 1 });
          if (container) {
            container.style.overflowY = "hidden";
            container.style.maxHeight = "100vh";
            container.style.maxWidth = "100vw";
          }
          document.body.style.overflowY = "hidden";
          document.body.style.maxHeight = "100vh";
          document.body.style.maxWidth = "100vw";

          requestAnimationFrame(() => {
            if (token !== activeToken) return;
            requestAnimationFrame(() => {
              if (token !== activeToken) return;
              router.push(href);
            });
          });
        },
      },
      "<+=25%"
    );
};
